/* eslint-disable max-lines */
import { ArrowPathIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import FormWrapper from "../../components/ui/FormWrapper";
import Input from "../../components/ui/input/Input";
import Select from "../../components/ui/input/Select";
import Textarea from "../../components/ui/input/Textarea";
import useFetch from "../../hooks/useFetch";
import useNavigateState from "../../hooks/useNavigateState";
import { IAffiliate } from "../../interfaces/endpoints/IAffiliates";
import {
  createAffiliate,
  generateAffiliatePromoCode,
  getAffiliateById,
  updateAffiliate
} from "../../services/Affiliates.service";
import { cx } from "../../utils/classnames";
import AffiliatesSchema from "../../utils/validations/Affiliates.schema";

const statusOptions = [
  { name: "Ne pritje", id: 0 },
  { name: "Aprovuar", id: 1 },
  { name: "Refuzuar", id: 2 },
  { name: "Pezulluar", id: 3 }
];

const AffiliateForm: React.FC<{ editMode?: boolean }> = ({ editMode }) => {
  const { id } = useParams();
  const stateCrumbs = useNavigateState(["Partneret affiliate"]);
  const { state } = useLocation() as { state: { page?: number } | null };
  const [isRotated, setIsRotated] = useState(false);
  const [generateError, setGenerateError] = useState("");

  const methods = useForm<Partial<IAffiliate>>({
    resolver: yupResolver(AffiliatesSchema),
    defaultValues: {
      esimDiscountPercentage: 5,
      busTicketDiscountPercentage: 5,
      esimCommissionPercentage: 10,
      busTicketCommissionPercentage: 5,
      allowMultipleUses: false,
      status: 0
    }
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    setError,
    formState: { errors }
  } = methods;
  const navigate = useNavigate();

  const affiliateName = watch("affiliateName");

  const { response } = useFetch(getAffiliateById, {
    initRequest: !!editMode,
    params: Number(id)
  });

  useEffect(() => {
    if (editMode && response && response.data) {
      const data = { ...response.data } as any;
      if (data.expirationDate) {
        data.expirationDate = dayjs(data.expirationDate).format("YYYY-MM-DD");
      }
      Object.entries(data).forEach(([key, value]) => {
        setValue(key as keyof IAffiliate, value as any);
      });
    }
  }, [response, id]);

  const handleGeneratePromoCode = async () => {
    setIsRotated(false);
    setGenerateError("");
    if (!affiliateName || affiliateName.trim().length === 0) {
      setGenerateError("Shkruani emrin per te gjeneruar kodin");
      return;
    }
    try {
      const promoCodeResponse = await generateAffiliatePromoCode(affiliateName.trim());
      setValue("promoCode", promoCodeResponse.data as any);
      setIsRotated(true);
    } catch {
      setGenerateError("Deshtoi gjenerimi i kodit");
    }
  };

  const handleSubmitError = (error: any) => {
    const responseData = error?.response?.data;
    const fieldErrors = responseData?.errors;

    if (fieldErrors) {
      Object.entries(fieldErrors).forEach(([field, messages]) => {
        const fieldKey = field.charAt(0).toLowerCase() + field.slice(1);
        setError(fieldKey as keyof IAffiliate, {
          message: (messages as string[])?.[0]
        });
      });
    } else {
      toast.error(
        responseData?.message || "Ndodhi nje gabim gjate ruajtjes"
      );
    }
  };

  const onSubmit: SubmitHandler<Partial<IAffiliate>> = async (data) => {
    if (editMode) {
      await updateAffiliate(data, Number(id))
        .then(() => {
          toast.success("Partneri u modifikua me sukses");
          setTimeout(
            () =>
              navigate(
                `/affiliates?pageindex=${state?.page ? state?.page : 1}`,
                { state: stateCrumbs }
              ),
            500
          );
        })
        .catch(handleSubmitError);
    } else {
      await createAffiliate(data)
        .then(() => {
          toast.success("Partneri u shtua me sukses");
          setTimeout(
            () =>
              navigate("/affiliates", {
                state: ["Partneret affiliate"]
              }),
            500
          );
        })
        .catch(handleSubmitError);
    }
  };

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/affiliates"
        state={["Partneret affiliate"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>
      <FormWrapper
        history={{ entityName: "Affiliate", entityId: id }}
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
        onSubmit={handleSubmit(onSubmit)}
        title={editMode ? "Modifiko partnerin" : "Shto partner te ri"}
      >
        <Input
          {...register("affiliateName")}
          label="Emri i plote"
          error={(errors as any).affiliateName?.message}
        />
        <Input
          {...register("email")}
          label="Email"
          error={(errors as any).email?.message}
        />
        <Input
          {...register("phoneNumber")}
          label="Numri i telefonit"
          error={(errors as any).phoneNumber?.message}
        />
        <div className="mb-6">
          <Input
            {...register("promoCode")}
            label="Kodi promocional"
            afterClassName="pointer-events-auto"
            after={
              <button
                onClick={handleGeneratePromoCode}
                type="button"
                className={cx([
                  "items-center rounded-md bg-gray-200 py-1.5 px-2 -mr-2",
                  "duration-300 transform-all ring-1 hover:shadow hover:ring-2 ring-gray-300"
                ])}
              >
                <ArrowPathIcon
                  className={cx([
                    "w-5 h-5 text-gray-700 transition-transform duration-300 ease-in-out transform",
                    isRotated && "rotate-180"
                  ])}
                />
              </button>
            }
            error={(errors as any).promoCode?.message}
          />
          {generateError && (
            <p className="text-red text-xs mt-1">{generateError}</p>
          )}
        </div>
        <div className="col-span-full flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="allowMultipleUses"
            {...register("allowMultipleUses")}
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="allowMultipleUses" className="text-sm text-gray-700">
            Lejo perdorimin e shumefishte te kodit nga i njejti perdorues
          </label>
        </div>
        <div className="col-span-full">
          <p className="text-sm font-semibold text-gray-700 mb-2">eSIM</p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              {...register("esimCommissionPercentage")}
              label="Komisioni eSIM (%)"
              type="number"
              error={(errors as any).esimCommissionPercentage?.message}
            />
            <Input
              {...register("esimDiscountPercentage")}
              label="Zbritja eSIM (%)"
              type="number"
              error={(errors as any).esimDiscountPercentage?.message}
            />
          </div>
        </div>
        <div className="col-span-full">
          <p className="text-sm font-semibold text-gray-700 mb-2">Bileta Autobusi</p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              {...register("busTicketCommissionPercentage")}
              label="Komisioni Bileta (%)"
              type="number"
              error={(errors as any).busTicketCommissionPercentage?.message}
            />
            <Input
              {...register("busTicketDiscountPercentage")}
              label="Zbritja Bileta (%)"
              type="number"
              error={(errors as any).busTicketDiscountPercentage?.message}
            />
          </div>
        </div>
        <Input
          {...register("expirationDate")}
          label="Data e skadimit"
          type="date"
          error={(errors as any).expirationDate?.message}
        />
        {editMode && (
          <Select
            {...register("status")}
            label="Statusi"
            options={statusOptions}
            error={(errors as any).status?.message}
            hasEmptyOption={false}
          />
        )}
        <Input {...register("website")} label="Website (opsionale)" />
        <Input
          {...register("socialMediaUrl")}
          label="Rrjete sociale (opsionale)"
        />
        <Input {...register("bankName")} label="Emri i bankes (opsionale)" />
        <Input {...register("iban")} label="IBAN (opsionale)" />
        <Input
          {...register("accountHolderName")}
          label="Mbajtesi i llogarise (opsionale)"
        />
        <Textarea
          {...register("adminNote")}
          label="Shenime nga admini (opsionale)"
        />
      </FormWrapper>
    </FormProvider>
  );
};

export default AffiliateForm;
